import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgFor, NgIf } from '@angular/common';
import { catchError, forkJoin, map, of } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import swal from 'sweetalert2';
import { ConsultaList } from 'src/app/shared/model/core/ConsultaList.model';
import { Aplicacion } from 'src/app/shared/model/fise/aplicacion.model';
import { ConfiguracionBean } from 'src/app/shared/model/fise/configuracionbean.model';
import { Servidor } from 'src/app/shared/model/fise/servidor.model';
import { UsuarioFise } from 'src/app/shared/model/security/userbean.model';
import { Usuario } from 'src/app/shared/model/security/usuario.model';
import { FiseService } from '../fise.service';
import { SoloDireccionesIpDirective } from '../../utilerias/directives/solo-direcciones-ip.directive';
import { SoloLetraEspacioNumeroAcentoDirective } from '../../utilerias/directives/solo-letra-espacio-numero-acento.directive';

@Component({
  selector: 'app-configuracion-aplicaciones-servidores',
  standalone: true,
  templateUrl: './configuracion-aplicaciones-servidores.component.html',
  imports: [
    FormsModule,
    NgFor,
    NgIf,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatOptionModule,
    MatSelectModule,
    SoloDireccionesIpDirective,
    SoloLetraEspacioNumeroAcentoDirective,
  ],
})
export class ConfiguracionAplicacionesServidoresComponent implements OnInit {
  listaUsuarios: Array<Usuario> = [];
  listaServidores: Array<Servidor> = [];
  listaAplicaciones: Array<Aplicacion> = [];
  listaUsuariosServidor: Array<UsuarioFise> = [];
  listaUsuariosApp: Array<UsuarioFise> = [];

  usuarioSelect = new Usuario();
  servidorSelect = new Servidor();
  aplicacionSelect = new Aplicacion();
  idUsuarioCos?: number;

  filtroUsuarioCos = '';
  filtroServidor = '';
  filtroAplicacion = '';
  filtroUsuarioServidor = '';
  filtroUsuarioApp = '';

  totalRegistrosUsuario = 0;
  totalRegistrosServidor = 0;
  totalRegistrosAplicacion = 0;
  totalRegistrosUsuarioServidor = 0;
  totalRegistrosUsuarioApp = 0;

  usersCosCurrentPage = 1;
  serversCurrentPage = 1;
  appsCurrentPage = 1;
  usersServerCurrentPage = 1;
  usersAppCurrentPage = 1;

  usersCosPageSize = 5;
  serversPageSize = 5;
  appsPageSize = 5;
  usersServerPageSize = 5;
  usersAppPageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];

  loadingUsuariosCos = false;
  loadingServidores = false;
  loadingAplicaciones = false;
  loadingUsuariosServidor = false;
  loadingUsuariosApp = false;

  private serverConfigIds = new Set<number>();
  private readonly usuariosCosSeleccionados = new Map<number, Usuario>();
  private readonly configuracionServidorPorUsuarioCos = new Map<number, Set<number>>();
  private readonly usuariosServidorConEstadoMixto = new Set<number>();

  constructor(
    private usuarioService: FiseService,
    private servidorService: FiseService,
    private aplicacionService: FiseService,
    private configService: FiseService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadUsuariosCos(1);
    this.loadServidores(1);
  }

  loadUsuariosCos(page: number): void {
    this.usersCosCurrentPage = Math.max(1, page);
    this.loadingUsuariosCos = this.listaUsuarios.length === 0;
    this.cdr.detectChanges();

    const usuarioReq = new Usuario();
    usuarioReq.user_nom = (this.filtroUsuarioCos ?? '').trim();

    const request = new ConsultaList<Usuario>();
    request.bean = usuarioReq;
    request.regInicial = (this.usersCosCurrentPage - 1) * this.usersCosPageSize;
    request.regXPagina = this.usersCosPageSize;

    forkJoin({
      total: this.usuarioService.ConsultaNumUsuarios(usuarioReq),
      usuarios: this.usuarioService.ObtenerUsuarioPorPerfil(request),
    }).subscribe({
      next: ({ total, usuarios }) => {
        this.totalRegistrosUsuario = this.resolveTotalRegistros(total, usuarios, this.usersCosCurrentPage, this.usersCosPageSize);
        this.listaUsuarios = Array.isArray(usuarios?.list) ? [...usuarios.list] : [];

        const maxPages = this.usersCosTotalPages;
        if (maxPages > 0 && this.usersCosCurrentPage > maxPages) {
          this.loadUsuariosCos(maxPages);
          return;
        }

        if (maxPages === 0) {
          this.usersCosCurrentPage = 1;
        }

        this.loadingUsuariosCos = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.listaUsuarios = [];
        this.totalRegistrosUsuario = 0;
        this.usersCosCurrentPage = 1;
        this.loadingUsuariosCos = false;
        this.cdr.detectChanges();
      },
    });
  }

  loadServidores(page: number): void {
    this.serversCurrentPage = Math.max(1, page);
    this.loadingServidores = this.listaServidores.length === 0;
    this.cdr.detectChanges();

    const servidorReq = new Servidor();
    servidorReq.ip = (this.filtroServidor ?? '').trim();

    const request = new ConsultaList<Servidor>();
    request.bean = servidorReq;
    request.regInicial = (this.serversCurrentPage - 1) * this.serversPageSize;
    request.regXPagina = this.serversPageSize;

    forkJoin({
      total: this.servidorService.ConsultaNumServidores(servidorReq),
      servidores: this.servidorService.consultarServidorPorIPPag(request),
    }).subscribe({
      next: ({ total, servidores }) => {
        this.totalRegistrosServidor = this.resolveTotalRegistros(total, servidores, this.serversCurrentPage, this.serversPageSize);
        this.listaServidores = Array.isArray(servidores?.list) ? [...servidores.list] : [];

        const maxPages = this.serversTotalPages;
        if (maxPages > 0 && this.serversCurrentPage > maxPages) {
          this.loadServidores(maxPages);
          return;
        }

        if (maxPages === 0) {
          this.serversCurrentPage = 1;
        }

        this.loadingServidores = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.listaServidores = [];
        this.totalRegistrosServidor = 0;
        this.serversCurrentPage = 1;
        this.loadingServidores = false;
        this.cdr.detectChanges();
      },
    });
  }

  loadAplicaciones(page: number): void {
    if (!this.servidorSelect?.id_Servidor) {
      this.clearAplicaciones();
      this.cdr.detectChanges();
      return;
    }

    this.appsCurrentPage = Math.max(1, page);
    this.loadingAplicaciones = this.listaAplicaciones.length === 0;
    this.cdr.detectChanges();

    const aplicacionReq = new Aplicacion();
    aplicacionReq.id_Servidor = this.servidorSelect.id_Servidor.toString();
    aplicacionReq.idTipo = (this.filtroAplicacion ?? '').trim();

    const request = new ConsultaList<Aplicacion>();
    request.bean = aplicacionReq;
    request.regInicial = (this.appsCurrentPage - 1) * this.appsPageSize;
    request.regXPagina = this.appsPageSize;

    forkJoin({
      total: this.aplicacionService.ConsultaNumAplicaciones(aplicacionReq),
      aplicaciones: this.servidorService.ObtenerAplicacionPorTipo(request),
    }).subscribe({
      next: ({ total, aplicaciones }) => {
        this.totalRegistrosAplicacion = this.resolveTotalRegistros(total, aplicaciones, this.appsCurrentPage, this.appsPageSize);
        this.listaAplicaciones = Array.isArray(aplicaciones?.list) ? [...aplicaciones.list] : [];

        const maxPages = this.appsTotalPages;
        if (maxPages > 0 && this.appsCurrentPage > maxPages) {
          this.loadAplicaciones(maxPages);
          return;
        }

        if (maxPages === 0) {
          this.appsCurrentPage = 1;
        }

        this.loadingAplicaciones = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.clearAplicaciones();
        this.loadingAplicaciones = false;
        this.cdr.detectChanges();
      },
    });
  }

  loadUsuariosServidor(page: number): void {
    const usuariosCosIds = this.usuariosCosSeleccionadosIds;
    if (!this.servidorSelect?.id_Servidor) {
      this.clearUsuariosServidor();
      this.cdr.detectChanges();
      return;
    }

    this.usersServerCurrentPage = Math.max(1, page);
    this.loadingUsuariosServidor = this.listaUsuariosServidor.length === 0;
    this.cdr.detectChanges();

    const usuarioReq = new UsuarioFise();
    usuarioReq.id_Servidor = this.servidorSelect.id_Servidor;
    usuarioReq.usuario = (this.filtroUsuarioServidor ?? '').trim();

    const request = new ConsultaList<UsuarioFise>();
    request.bean = usuarioReq;
    request.regInicial = (this.usersServerCurrentPage - 1) * this.usersServerPageSize;
    request.regXPagina = this.usersServerPageSize;

    const configuraciones = usuariosCosIds.map((idUsuarioCos) => {
      const configReq = new ConfiguracionBean();
      configReq.id_Servidor = this.servidorSelect.id_Servidor;
      configReq.id = idUsuarioCos;
      return this.servidorService.ObtenerConfiguracionUsuariosSER(configReq).pipe(
        // La disponibilidad de la tabla no debe depender de una configuración individual.
        catchError(() => of({ list: [] as number[] }))
      );
    });

    // Para una sola selección se conserva una llamada directa al backend. `forkJoin`
    // se utiliza únicamente cuando hay varias configuraciones que combinar.
    const configuracionesServidor$ =
      configuraciones.length === 0
        ? of([])
        : configuraciones.length === 1
        ? configuraciones[0].pipe(map((configuracion) => [configuracion]))
        : forkJoin(configuraciones);

    forkJoin({
      total: this.servidorService.contarUsuarioPorSER(usuarioReq),
      usuarios: this.servidorService.obtenerUsuarioPorSERPag(request),
      config: configuracionesServidor$,
    }).subscribe({
      next: ({ total, usuarios, config }) => {
        this.totalRegistrosUsuarioServidor = this.resolveTotalRegistros(total, usuarios, this.usersServerCurrentPage, this.usersServerPageSize);
        this.configuracionServidorPorUsuarioCos.clear();
        config.forEach((configuracion, index) => {
          this.configuracionServidorPorUsuarioCos.set(
            usuariosCosIds[index],
            new Set(Array.isArray(configuracion?.list) ? configuracion.list : [])
          );
        });
        this.serverConfigIds = new Set(
          Array.from(this.configuracionServidorPorUsuarioCos.values()).flatMap((configuracion) => Array.from(configuracion))
        );
        this.usuariosServidorConEstadoMixto.clear();
        this.listaUsuariosServidor = Array.isArray(usuarios?.list)
          ? usuarios.list.map((usuarioBean) => {
              const configuracionesPorUsuario = usuariosCosIds.map((idUsuarioCos) =>
                this.configuracionServidorPorUsuarioCos.get(idUsuarioCos)?.has(usuarioBean.id) ?? false
              );
              const todosActivos = usuariosCosIds.length > 0 && configuracionesPorUsuario.every(Boolean);
              const algunoActivo = configuracionesPorUsuario.some(Boolean);

              if (algunoActivo && !todosActivos) {
                this.usuariosServidorConEstadoMixto.add(usuarioBean.id);
              }

              return { ...usuarioBean, check: todosActivos };
            })
          : [];

        const maxPages = this.usersServerTotalPages;
        if (maxPages > 0 && this.usersServerCurrentPage > maxPages) {
          this.loadUsuariosServidor(maxPages);
          return;
        }

        if (maxPages === 0) {
          this.usersServerCurrentPage = 1;
        }

        this.loadingUsuariosServidor = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.clearUsuariosServidor();
        this.loadingUsuariosServidor = false;
        this.cdr.detectChanges();
      },
    });
  }

  loadUsuariosApp(page: number): void {
    if (!this.aplicacionSelect?.id) {
      this.clearUsuariosApp();
      this.cdr.detectChanges();
      return;
    }

    this.usersAppCurrentPage = Math.max(1, page);
    this.loadingUsuariosApp = this.listaUsuariosApp.length === 0;
    this.cdr.detectChanges();

    const usuarioReq = new UsuarioFise();
    usuarioReq.id_Aplicacion = this.aplicacionSelect.id;
    usuarioReq.usuario = (this.filtroUsuarioApp ?? '').trim();

    const request = new ConsultaList<UsuarioFise>();
    request.bean = usuarioReq;
    request.regInicial = (this.usersAppCurrentPage - 1) * this.usersAppPageSize;
    request.regXPagina = this.usersAppPageSize;

    const configuracionUsuariosApp$ = this.idUsuarioCos
      ? (() => {
          const configReq = new ConfiguracionBean();
          configReq.id_Aplicacion = this.aplicacionSelect.id;
          configReq.id = this.idUsuarioCos;
          return this.servidorService.ObtenerConfiguracionUsuariosApp(configReq);
        })()
      : of({ list: [] as number[] });

    forkJoin({
      total: this.aplicacionService.contarUsuarioPorApp(usuarioReq),
      usuarios: this.servidorService.obtenerUsuarioPorAppPag(request),
      config: configuracionUsuariosApp$,
    }).subscribe({
      next: ({ total, usuarios, config }) => {
        this.totalRegistrosUsuarioApp = this.resolveTotalRegistros(total, usuarios, this.usersAppCurrentPage, this.usersAppPageSize);
        const appConfigIds = new Set(Array.isArray(config?.list) ? config.list : []);
        this.listaUsuariosApp = Array.isArray(usuarios?.list)
          ? usuarios.list.map((usuarioBean) => ({ ...usuarioBean, check: appConfigIds.has(usuarioBean.id) }))
          : [];

        const maxPages = this.usersAppTotalPages;
        if (maxPages > 0 && this.usersAppCurrentPage > maxPages) {
          this.loadUsuariosApp(maxPages);
          return;
        }

        if (maxPages === 0) {
          this.usersAppCurrentPage = 1;
        }

        this.loadingUsuariosApp = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.clearUsuariosApp();
        this.loadingUsuariosApp = false;
        this.cdr.detectChanges();
      },
    });
  }

  onUsuarioCosFilterChange(): void {
    this.loadUsuariosCos(1);
  }

  onServidorFilterChange(): void {
    this.loadServidores(1);
  }

  onAplicacionFilterChange(): void {
    this.aplicacionSelect = new Aplicacion();
    this.clearUsuariosApp();
    this.loadAplicaciones(1);
  }

  onUsuarioServidorFilterChange(): void {
    this.loadUsuariosServidor(1);
  }

  onUsuarioAppFilterChange(): void {
    this.loadUsuariosApp(1);
  }

  onSelectUsuarioCos(usuario: Usuario): void {
    if (usuario?.id == null) {
      return;
    }

    if (this.usuariosCosSeleccionados.has(usuario.id)) {
      this.usuariosCosSeleccionados.delete(usuario.id);
    } else {
      this.usuariosCosSeleccionados.set(usuario.id, usuario);
    }

    this.sincronizarUsuarioCosPrincipal();
    this.loadUsuariosServidor(1);
  }

  onCambioSeleccionUsuarioCos(usuario: Usuario, seleccionado: boolean): void {
    if (usuario?.id == null || this.usuariosCosSeleccionados.has(usuario.id) === seleccionado) {
      return;
    }

    this.onSelectUsuarioCos(usuario);
  }

  onSelectServidor(servidor: Servidor): void {
    if (!servidor?.id_Servidor || this.servidorSelect?.id_Servidor === servidor.id_Servidor) {
      return;
    }

    this.servidorSelect = servidor;
    this.filtroAplicacion = '';
    this.loadUsuariosServidor(1);
    this.loadAplicaciones(1);
  }

  onSelectAplicacion(aplicacion: Aplicacion): void {
    if (!this.servidorSelect?.id_Servidor || this.aplicacionSelect?.id === aplicacion?.id) {
      return;
    }

    this.aplicacionSelect = aplicacion;
    this.filtroUsuarioApp = '';
    this.loadUsuariosApp(1);
  }

  onClicBoxServer(usuarioObj: UsuarioFise, usuarioEstado: boolean): void {
    const usuariosCosIds = this.usuariosCosSeleccionadosIds;
    if (usuariosCosIds.length === 0 || !this.servidorSelect?.id_Servidor) {
      return;
    }

    if (usuarioEstado) {
      const altasPendientes = usuariosCosIds
        .filter((idUsuarioCos) => !this.configuracionServidorPorUsuarioCos.get(idUsuarioCos)?.has(usuarioObj.id))
        .map((idUsuarioCos) => this.crearConfiguracionServidor(idUsuarioCos, usuarioObj.id));

      if (altasPendientes.length === 0) {
        this.loadUsuariosServidor(this.usersServerCurrentPage);
        return;
      }

      forkJoin(altasPendientes).subscribe({
        next: () => this.loadUsuariosServidor(this.usersServerCurrentPage),
        error: () => this.loadUsuariosServidor(this.usersServerCurrentPage),
      });
      return;
    }

    const configuracionesActivas = usuariosCosIds.filter((idUsuarioCos) =>
      this.configuracionServidorPorUsuarioCos.get(idUsuarioCos)?.has(usuarioObj.id)
    );

    if (configuracionesActivas.length === 0) {
      this.loadUsuariosServidor(this.usersServerCurrentPage);
      return;
    }

    swal.fire({
      title: 'Alerta',
      text: configuracionesActivas.length > 1
        ? '¿Desea borrar la configuración de usuario-servidor para todos los usuarios COS seleccionados?'
        : '¿Desea borrar la configuración de usuario-servidor?',
      buttonsStyling: false,
      icon: 'warning',
      customClass: {
        confirmButton: 'btn btn-success',
        cancelButton: 'btn btn-info',
      },
      confirmButtonText: 'Sí',
      cancelButtonText: 'Cancelar',
      showConfirmButton: true,
      showCancelButton: true,
    }).then((result) => {
      if (!result.value) {
        this.loadUsuariosServidor(this.usersServerCurrentPage);
        return;
      }

      const bajas = configuracionesActivas.map((idUsuarioCos) => {
        const solicitud = this.crearSolicitudConfiguracionServidor(idUsuarioCos, usuarioObj.id);
        const configuracionesDelUsuario = this.configuracionServidorPorUsuarioCos.get(idUsuarioCos)?.size ?? 0;
        return configuracionesDelUsuario <= 1
          ? this.configService.BorrarConfiguracionServerApp(solicitud)
          : this.configService.BorrarConfiguracionServer(solicitud);
      });

      forkJoin(bajas).subscribe({
        next: () => this.loadUsuariosServidor(this.usersServerCurrentPage),
        error: () => this.loadUsuariosServidor(this.usersServerCurrentPage),
      });
    });
  }

  onClicBoxServerIndividual(usuarioObj: UsuarioFise, usuarioEstado: boolean): void {
    if (!this.idUsuarioCos || !this.servidorSelect?.id_Servidor) {
      return;
    }

    const req = new ConfiguracionBean();
    req.id = this.idUsuarioCos;
    req.id_Usuario = usuarioObj.id;
    req.id_Servidor = this.servidorSelect.id_Servidor;

    if (usuarioEstado) {
      this.configService.GuaradarConfiguracionUsuarioPorServer(req).subscribe(() => {
        this.loadUsuariosServidor(this.usersServerCurrentPage);
        if (this.aplicacionSelect?.id) {
          this.loadUsuariosApp(this.usersAppCurrentPage);
        }
      });
      return;
    }

    swal.fire({
      title: 'Alerta',
      text: this.serverConfigIds.size <= 1
        ? '¿Desea borrar la configuración de usuario-servidor y sus configuraciones por aplicación?'
        : '¿Desea borrar la configuración de usuario-servidor?',
      buttonsStyling: false,
      icon: 'warning',
      customClass: {
        confirmButton: 'btn btn-success',
        cancelButton: 'btn btn-info',
      },
      confirmButtonText: 'Si',
      cancelButtonText: 'Cancelar',
      showConfirmButton: true,
      showCancelButton: true,
    }).then((result) => {
      if (!result.value) {
        usuarioObj.check = true;
        this.cdr.detectChanges();
        return;
      }

      const deleteRequest = this.serverConfigIds.size <= 1
        ? this.configService.BorrarConfiguracionServerApp(req)
        : this.configService.BorrarConfiguracionServer(req);

      deleteRequest.subscribe((response) => {
        if (response?.pCodRetorno !== '00000') {
          usuarioObj.check = true;
          this.cdr.detectChanges();
          return;
        }

        if (this.serverConfigIds.size <= 1) {
          this.aplicacionSelect = new Aplicacion();
          this.clearUsuariosApp();
        } else if (this.aplicacionSelect?.id) {
          this.loadUsuariosApp(this.usersAppCurrentPage);
        }

        this.loadUsuariosServidor(this.usersServerCurrentPage);
        swal.fire({
          title: 'ÉXITO',
          text: response.pMensaje,
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-success',
            cancelButton: 'btn btn-info',
          },
          confirmButtonText: 'Aceptar',
          icon: 'success',
        });
      });
    });
  }

  onClicBoxUsuarioAplicacion(usuarioObj: UsuarioFise, usuarioEstado: boolean): void {
    if (!this.idUsuarioCos || !this.aplicacionSelect?.id) {
      return;
    }

    const req = new ConfiguracionBean();
    req.id = this.idUsuarioCos;
    req.id_Usuario = usuarioObj.id;
    req.id_Aplicacion = usuarioObj.id_Aplicacion;

    if (usuarioEstado) {
      this.configService.GuaradarConfiguracionUsuarioPorAplicacion(req).subscribe(() => {
        this.loadUsuariosApp(this.usersAppCurrentPage);
      });
      return;
    }

    swal.fire({
      title: 'Alerta',
      text: '¿Desea borrar la configuración de usuario-aplicación?',
      buttonsStyling: false,
      icon: 'warning',
      customClass: {
        confirmButton: 'btn btn-success',
        cancelButton: 'btn btn-info',
      },
      confirmButtonText: 'Si',
      cancelButtonText: 'Cancelar',
      showConfirmButton: true,
      showCancelButton: true,
    }).then((result) => {
      if (!result.value) {
        usuarioObj.check = true;
        this.cdr.detectChanges();
        return;
      }

      this.configService.BorrarConfiguracionApp(req).subscribe((response) => {
        if (response?.pCodRetorno !== '00000') {
          usuarioObj.check = true;
          this.cdr.detectChanges();
          return;
        }

        this.loadUsuariosApp(this.usersAppCurrentPage);
        swal.fire({
          title: 'ÉXITO',
          text: response.pMensaje,
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-success',
            cancelButton: 'btn btn-info',
          },
          confirmButtonText: 'Aceptar',
          icon: 'success',
        });
      });
    });
  }

  onUsersCosPageSizeChange(value: number): void {
    this.usersCosPageSize = Number(value);
    this.loadUsuariosCos(1);
  }

  onServersPageSizeChange(value: number): void {
    this.serversPageSize = Number(value);
    this.loadServidores(1);
  }

  onAppsPageSizeChange(value: number): void {
    this.appsPageSize = Number(value);
    this.loadAplicaciones(1);
  }

  onUsersServerPageSizeChange(value: number): void {
    this.usersServerPageSize = Number(value);
    this.loadUsuariosServidor(1);
  }

  onUsersAppPageSizeChange(value: number): void {
    this.usersAppPageSize = Number(value);
    this.loadUsuariosApp(1);
  }

  onUsersCosPageChange(page: number): void {
    if (page === this.usersCosCurrentPage || page < 1 || page > this.usersCosTotalPages) {
      return;
    }
    this.loadUsuariosCos(page);
  }

  onServersPageChange(page: number): void {
    if (page === this.serversCurrentPage || page < 1 || page > this.serversTotalPages) {
      return;
    }
    this.loadServidores(page);
  }

  onAppsPageChange(page: number): void {
    if (page === this.appsCurrentPage || page < 1 || page > this.appsTotalPages) {
      return;
    }
    this.loadAplicaciones(page);
  }

  onUsersServerPageChange(page: number): void {
    if (page === this.usersServerCurrentPage || page < 1 || page > this.usersServerTotalPages) {
      return;
    }
    this.loadUsuariosServidor(page);
  }

  onUsersAppPageChange(page: number): void {
    if (page === this.usersAppCurrentPage || page < 1 || page > this.usersAppTotalPages) {
      return;
    }
    this.loadUsuariosApp(page);
  }

  firstUsersCosPage(): void { this.onUsersCosPageChange(1); }
  previousUsersCosPage(): void { this.onUsersCosPageChange(this.usersCosCurrentPage - 1); }
  nextUsersCosPage(): void { this.onUsersCosPageChange(this.usersCosCurrentPage + 1); }
  lastUsersCosPage(): void { this.onUsersCosPageChange(this.usersCosTotalPages); }

  firstServersPage(): void { this.onServersPageChange(1); }
  previousServersPage(): void { this.onServersPageChange(this.serversCurrentPage - 1); }
  nextServersPage(): void { this.onServersPageChange(this.serversCurrentPage + 1); }
  lastServersPage(): void { this.onServersPageChange(this.serversTotalPages); }

  firstAppsPage(): void { this.onAppsPageChange(1); }
  previousAppsPage(): void { this.onAppsPageChange(this.appsCurrentPage - 1); }
  nextAppsPage(): void { this.onAppsPageChange(this.appsCurrentPage + 1); }
  lastAppsPage(): void { this.onAppsPageChange(this.appsTotalPages); }

  firstUsersServerPage(): void { this.onUsersServerPageChange(1); }
  previousUsersServerPage(): void { this.onUsersServerPageChange(this.usersServerCurrentPage - 1); }
  nextUsersServerPage(): void { this.onUsersServerPageChange(this.usersServerCurrentPage + 1); }
  lastUsersServerPage(): void { this.onUsersServerPageChange(this.usersServerTotalPages); }

  firstUsersAppPage(): void { this.onUsersAppPageChange(1); }
  previousUsersAppPage(): void { this.onUsersAppPageChange(this.usersAppCurrentPage - 1); }
  nextUsersAppPage(): void { this.onUsersAppPageChange(this.usersAppCurrentPage + 1); }
  lastUsersAppPage(): void { this.onUsersAppPageChange(this.usersAppTotalPages); }

  get usersCosTotalPages(): number { return this.totalRegistrosUsuario > 0 ? Math.ceil(this.totalRegistrosUsuario / this.usersCosPageSize) : 0; }
  get usuariosCosSeleccionadosCantidad(): number { return this.usuariosCosSeleccionados.size; }
  get serversTotalPages(): number { return this.totalRegistrosServidor > 0 ? Math.ceil(this.totalRegistrosServidor / this.serversPageSize) : 0; }
  get appsTotalPages(): number { return this.totalRegistrosAplicacion > 0 ? Math.ceil(this.totalRegistrosAplicacion / this.appsPageSize) : 0; }
  get usersServerTotalPages(): number { return this.totalRegistrosUsuarioServidor > 0 ? Math.ceil(this.totalRegistrosUsuarioServidor / this.usersServerPageSize) : 0; }
  get usersAppTotalPages(): number { return this.totalRegistrosUsuarioApp > 0 ? Math.ceil(this.totalRegistrosUsuarioApp / this.usersAppPageSize) : 0; }

  get usersCosRangeEnd(): number { return Math.min(this.usersCosCurrentPage * this.usersCosPageSize, this.totalRegistrosUsuario); }
  get serversRangeEnd(): number { return Math.min(this.serversCurrentPage * this.serversPageSize, this.totalRegistrosServidor); }
  get appsRangeEnd(): number { return Math.min(this.appsCurrentPage * this.appsPageSize, this.totalRegistrosAplicacion); }
  get usersServerRangeEnd(): number { return Math.min(this.usersServerCurrentPage * this.usersServerPageSize, this.totalRegistrosUsuarioServidor); }
  get usersAppRangeEnd(): number { return Math.min(this.usersAppCurrentPage * this.usersAppPageSize, this.totalRegistrosUsuarioApp); }

  get showUsersCosPagination(): boolean { return this.totalRegistrosUsuario > 0; }
  get showServersPagination(): boolean { return this.totalRegistrosServidor > 0; }
  get showAppsPagination(): boolean { return this.totalRegistrosAplicacion > 0; }
  get showUsersServerPagination(): boolean { return this.totalRegistrosUsuarioServidor > 0; }
  get showUsersAppPagination(): boolean { return this.totalRegistrosUsuarioApp > 0; }
  get hasServerConfig(): boolean { return this.serverConfigIds.size > 0; }

  isUsuarioCosSeleccionado(usuario: Usuario): boolean {
    return usuario?.id != null && this.usuariosCosSeleccionados.has(usuario.id);
  }

  isUsuarioServidorConEstadoMixto(usuario: UsuarioFise): boolean {
    return !!usuario?.id && this.usuariosServidorConEstadoMixto.has(usuario.id);
  }

  getUsersCosPaginationItems(): Array<number | string> { return this.getPaginationItems(this.usersCosTotalPages, this.usersCosCurrentPage); }
  getServersPaginationItems(): Array<number | string> { return this.getPaginationItems(this.serversTotalPages, this.serversCurrentPage); }
  getAppsPaginationItems(): Array<number | string> { return this.getPaginationItems(this.appsTotalPages, this.appsCurrentPage); }
  getUsersServerPaginationItems(): Array<number | string> { return this.getPaginationItems(this.usersServerTotalPages, this.usersServerCurrentPage); }
  getUsersAppPaginationItems(): Array<number | string> { return this.getPaginationItems(this.usersAppTotalPages, this.usersAppCurrentPage); }

  isPageNumber(item: number | string): item is number { return typeof item === 'number'; }

  getAplicacionesEmptyTitle(): string {
    return this.servidorSelect?.id_Servidor ? 'No se encontraron aplicaciones' : 'Seleccione un servidor';
  }

  getAplicacionesEmptyMessage(): string {
    return this.servidorSelect?.id_Servidor
      ? 'Ajuste el filtro para consultar otras aplicaciones del servidor seleccionado.'
      : 'Seleccione un servidor para habilitar la configuración por aplicación.';
  }

  getUsuariosServidorEmptyTitle(): string {
    if (!this.idUsuarioCos && !this.servidorSelect?.id_Servidor) {
      return 'Seleccione un usuario';
    }
    return this.servidorSelect?.id_Servidor ? 'No se encontraron usuarios de servidor' : 'Seleccione un servidor';
  }

  getUsuariosServidorEmptyMessage(): string {
    if (!this.idUsuarioCos && !this.servidorSelect?.id_Servidor) {
      return 'Primero seleccione el usuario COS al que se le configurarán accesos.';
    }
    return this.servidorSelect?.id_Servidor
      ? 'Ajuste el filtro para consultar otros usuarios asociados al servidor.'
      : 'Seleccione un servidor para cargar sus usuarios configurables.';
  }

  getUsuariosAppEmptyTitle(): string {
    return this.aplicacionSelect?.id ? 'No se encontraron usuarios de aplicación' : 'Seleccione una aplicación';
  }

  getUsuariosAppEmptyMessage(): string {
    return this.aplicacionSelect?.id
      ? 'Ajuste el filtro para consultar otros usuarios asociados a la aplicación.'
      : 'Seleccione una aplicación para administrar sus usuarios.';
  }

  private get usuariosCosSeleccionadosIds(): number[] {
    return Array.from(this.usuariosCosSeleccionados.keys());
  }

  private sincronizarUsuarioCosPrincipal(): void {
    const usuarioPrincipal = this.usuariosCosSeleccionados.values().next().value as Usuario | undefined;
    this.usuarioSelect = usuarioPrincipal ?? new Usuario();
    this.idUsuarioCos = usuarioPrincipal?.id;
  }

  private crearSolicitudConfiguracionServidor(idUsuarioCos: number, idUsuarioServidor: number): ConfiguracionBean {
    const solicitud = new ConfiguracionBean();
    solicitud.id = idUsuarioCos;
    solicitud.id_Usuario = idUsuarioServidor;
    solicitud.id_Servidor = this.servidorSelect.id_Servidor;
    return solicitud;
  }

  private crearConfiguracionServidor(idUsuarioCos: number, idUsuarioServidor: number) {
    return this.configService.GuaradarConfiguracionUsuarioPorServer(
      this.crearSolicitudConfiguracionServidor(idUsuarioCos, idUsuarioServidor)
    );
  }

  private clearSelectionsFromUser(): void {
    this.servidorSelect = new Servidor();
    this.filtroServidor = '';
    this.clearAplicacionSelection();
    this.clearUsuariosServidor();
    this.serverConfigIds = new Set<number>();
  }

  private clearSelectionsFromServer(): void {
    this.servidorSelect = new Servidor();
    this.clearAplicacionSelection();
    this.clearUsuariosServidor();
    this.serverConfigIds = new Set<number>();
  }

  private clearAplicacionSelection(): void {
    this.aplicacionSelect = new Aplicacion();
    this.filtroAplicacion = '';
    this.clearAplicaciones();
    this.clearUsuariosApp();
  }

  private clearAplicaciones(): void {
    this.listaAplicaciones = [];
    this.totalRegistrosAplicacion = 0;
    this.appsCurrentPage = 1;
    this.loadingAplicaciones = false;
  }

  private clearUsuariosServidor(): void {
    this.listaUsuariosServidor = [];
    this.totalRegistrosUsuarioServidor = 0;
    this.usersServerCurrentPage = 1;
    this.loadingUsuariosServidor = false;
  }

  private clearUsuariosApp(): void {
    this.listaUsuariosApp = [];
    this.totalRegistrosUsuarioApp = 0;
    this.usersAppCurrentPage = 1;
    this.loadingUsuariosApp = false;
  }

  private resetDependenciasUsuario(): void {
    this.idUsuarioCos = undefined;
    this.usuarioSelect = new Usuario();
    this.clearSelectionsFromUser();
    this.serverConfigIds = new Set<number>();
  }

  private getPaginationItems(totalPages: number, currentPage: number): Array<number | string> {
    if (totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  private resolveTotalRegistros(
    total: unknown,
    response: ConsultaList<Usuario> | ConsultaList<Servidor> | ConsultaList<Aplicacion> | ConsultaList<UsuarioFise>,
    currentPage: number,
    pageSize: number
  ): number {
    const totalResponse = total as Record<string, unknown> | null;
    const responseList = response as (ConsultaList<Usuario> | ConsultaList<Servidor> | ConsultaList<Aplicacion> | ConsultaList<UsuarioFise>) & Record<string, unknown>;
    const candidates = [
      totalResponse?.pTotalItems,
      totalResponse?.totalItems,
      totalResponse?.totalRegistros,
      responseList?.pTotalItems,
      responseList?.totalItems,
      responseList?.totalRegistros,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    const currentItems = Array.isArray(response?.list) ? response.list.length : 0;
    if (currentItems === 0) {
      return 0;
    }

    return (currentPage - 1) * pageSize + currentItems;
  }
}
